import React, { FC, createContext, useContext } from 'react';
import * as Routes from 'app/constants/Routes';
import { IWordsRoutes, UKnowRoutes, WordsRoutes } from 'app/constants/Routes/words';
import { OrderedMap } from 'immutable';
import ErrorBoundaryPage from './ErrorBoundaryPage';
import { useQuery } from 'app/components/Contexts';
import Redirect from 'app/components/router/Redirect';
import SiteWideRedirectMiddleware from 'app/components/router/SiteWideRedirectMiddleware';
import { IActiveRoute } from 'app/components/router/ActiveRoute';
import IRoute from 'app/constants/Routes/IRoute';

/* eslint-disable sort-imports */
// Start lazy wrappers
import makeLazyPage, { makeLazyPageBase } from 'app/components/layout/LazyLoadingPage';
import makeTeacherRecruitmentLazyLoadingPage from 'app/components/teacherRecruitment/LazyLoadingTeacherRecruitmentPage';
import makeUknowLazyLoadingPage from 'app/components/uknow/ui/LazyLoadingUknowPage';
// End lazy wrappers

// Start non-lazy routes
import DmmLoginCallbackPage from 'app/components/pages/DmmLoginCallback';
import OauthAttachCallbackPage from 'app/components/pages/OauthAttachCallback';
import OauthDelegatePage from 'app/components/pages/OauthDelegate';
import OauthLoginCallbackPage from 'app/components/pages/OauthLoginCallback';
import Login from 'app/components/pages/Login';
import Logout from 'app/containers/auth/Logout';
// End non-lazy routes

// Start lazy routes
const BlockedEmailDomainEditor = makeLazyPage(() =>
  import('app/components/editor/blockedEmailDomainList/BlockedEmailDomainEditor'));
const CreateUserPage = makeLazyPage(() =>
  import('app/components/users/CreatePage'));
const EditUserPage = makeLazyPage(() =>
  import('app/components/users/IntermediaryUserPage'));
const EditProfilePage = makeLazyPage(() =>
  import('app/containers/users/ProfilePage'));
const IncompleteProfilePage = makeLazyPage(() =>
  import('app/containers/users/IncompleteProfilePage'));
const ForgotPassword = makeLazyPage(() =>
  import('app/components/pages/ForgotPassword'));
const ResetPassword = makeLazyPage(() =>
  import('app/components/pages/ResetPassword'));
const EditStaffPage = makeLazyPage(() =>
  import('app/containers/staff/EditPage'));
const StaffProfilePage = makeLazyPage(() =>
  import('app/containers/staff/ProfilePage'));
const Dashboard = makeLazyPage(() =>
  import('app/components/pages/Dashboard'));
const Editor = makeLazyPage(() =>
  import('app/containers/editor/Editor'));
const CommonTextListPage = makeLazyPage(() =>
  import('app/components/editor/commonTextList/CommonTextListPage'));
const CommonTextEditor = makeLazyPage(() =>
  import('app/containers/editor/commonTextList/CommonTextEditorPage'));
const DialogSpeakerListPage = makeLazyPage(() =>
  import('app/components/editor/dialogSpeakerList/DialogSpeakerListPage'));
const DialogSpeakerEditorPage = makeLazyPage(() =>
  import('app/containers/editor/dialogSpeakerList/DialogSpeakerEditorPage'));
const CategoryEditorPage = makeLazyPage(() =>
  import('app/containers/editor/categoryEditor/CategoryEditorPage'));
const CategoryCoursesEditorPage = makeLazyPage(() =>
  import('app/containers/editor/categoryEditor/CategoryCoursesEditorPage'));
const CourseListPage = makeLazyPage(() =>
  import('app/components/editor/courseList/CourseListPage'));
const CourseEditorPage = makeLazyPage(() =>
  import('app/containers/editor/courseEditor/CourseEditorPage'));
const CourseLessonsEditorPage = makeLazyPage(() =>
  import('app/containers/editor/courseEditor/CourseLessonsEditorPage'));
const LessonListPage = makeLazyPage(() =>
  import('app/components/editor/lessonList/LessonListPage'));
const LessonTemplateListPage = makeLazyPage(() =>
  import('app/components/editor/lessonTemplateList/LessonTemplateListPage'));
const BannerListPage = makeLazyPage(() =>
  import('app/components/editor/bannerList/BannerListPage'));
const BannerEditorPage = makeLazyPage(() =>
  import('app/components/editor/bannerEditor/BannerEditorPage'));
const Users = makeLazyPage(() =>
  import('app/components/pages/users'));
const StaffMembers = makeLazyPage(() =>
  import('app/components/pages/staffMembers'));
const LessonPage = makeLazyPage(() =>
  import('app/containers/lesson/LessonPage'));
const PublishLesson = makeLazyPage(() =>
  import('app/containers/publishedLessonAdmin/PublishLesson'));
const Forbidden = makeLazyPage(() =>
  import('app/components/pages/Forbidden'));
const Maintenance = makeLazyPage(() =>
  import('app/components/pages/Maintenance'));
const CreateBrandMeetings = makeLazyPage(async () => {
  const m = await import('app/components/call/meetings/CreateMeetings');
  return { default: m.CreateBrandMeetings };
});
const LessonBookingsAdminPage = makeLazyPage(() =>
  import('app/containers/call/admin/LessonBookingsAdminPage'));
const CreateLessonBookingPage = makeLazyPage(() =>
  import('app/components/call/admin/CreateLessonBookingPage'));
const ViewLessonBookingPage = makeLazyPage(() =>
  import('app/components/call/admin/ViewLessonBookingPage'));
const SlackIntegrationSuccessPage = makeLazyPage(() =>
  import('app/components/pages/slack/SlackIntegrationSuccess'));
const SlackIntegrationCancelledPage = makeLazyPage(() =>
  import('app/components/pages/slack/SlackIntegrationCancelled'));
const SlackIntegrationFailurePage = makeLazyPage(() =>
  import('app/components/pages/slack/SlackIntegrationFailure'));
const OrganizationEditorPage = makeLazyPage(() =>
  import('app/components/editor/organizations/OrganizationEditorPage'));
const OrganizationListPage = makeLazyPage(() =>
  import('app/components/editor/organizations/OrganizationListPage'));
const CategoriesListPage = makeLazyPage(() =>
  import('app/components/editor/categoryList/CategoriesListPage'));
const CurriculaListPage = makeLazyPage(() =>
  import('app/components/editor/organizations/curricula/CurriculaListPage'));
const CurriculaEditorPage = makeLazyPage(() =>
  import('app/components/editor/organizations/curricula/CurriculaEditorPage'));
const OrganizationCreatePage = makeLazyPage(() =>
  import('app/components/editor/organizations/OrganizationCreatePage'));
const BrandEditorPage = makeLazyPage(() =>
  import('app/components/editor/organizations/BrandEditorPage'));
const BrandCreatePage = makeLazyPage(() =>
  import('app/components/editor/organizations/BrandCreatePage'));
const SentenceEditorPage = makeLazyPage(() =>
  import('app/components/editor/GlobalSentenceEditorPage'));
const SentenceListPage = makeLazyPage(() =>
  import('app/components/editor/GlobalSentenceListPage'));
const EditWordPage = makeLazyPage(() =>
  import('app/components/editor/wordList/WordEditor'));
const WordAdminPage = makeLazyPage(() =>
  import('app/components/editor/wordList/WordAdminPage'));
const VocabListAdminPage = makeLazyPage(() =>
  import('app/components/editor/vocabListList/VocabListListPage'));
const ViewVocabListPage = makeLazyPage(() =>
  import('app/components/editor/VocabListPage'));
const LessonBookingRedirectPage = makeLazyPage(() =>
  import('app/components/pages/LessonBookingRedirect'));
const MeetingBookingNewPage = makeLazyPage(() =>
  import('app/components/pages/MeetingBookingNew'));
const MeetingBookingRedirectPage = makeLazyPage(() =>
  import('app/components/pages/MeetingBookingRedirect'));
const FullScreenCallPage = makeLazyPage(() =>
  import('app/components/call/FullScreenCallPage'));
const PdfCallPage = makeLazyPage(() =>
  import('app/components/call/PdfCallPage'));
export const NotFound = makeLazyPage(() =>
  import('app/components/pages/NotFound'));
const ColorPalette = makeLazyPage(() =>
  import('app/components/pages/ColorPalette'));
const TestCallPage = makeLazyPage(() =>
  import('app/components/call/TestCallPage'));
const CategoriesPage = makeLazyPage(() =>
  import('app/components/materials/CategoriesPage'));
const CategoryPage = makeLazyPage(() =>
  import('app/components/materials/CategoryPage'));
const LevelsPage = makeLazyPage(() =>
  import('app/components/materials/LevelsPage'));
const OauthApplicationsPage = makeLazyPage(() =>
  import('app/containers/oauth/OauthApplicationsPage'));
const TeacherListPage = makeLazyPage(() =>
  import('app/components/teachers/TeacherListPage'));
const TeacherViewPage = makeLazyPage(() =>
  import('app/containers/teachers/ProfilePage'));
const WrongBrandPage = makeLazyPage(() =>
  import('app/components/pages/WrongBrandPage'));
const UserViewPage = makeLazyPage(() =>
  import('app/containers/users/ProfileViewPage'));
const OauthLoginPopupCallbackPage = makeLazyPage(() =>
  import('app/components/pages/OauthLoginPopupCallback'));
const AttachDmmAuthPage = makeLazyPage(() =>
  import('app/components/users/AttachDmmAuthPage'));
const LessonBookingTranscriptPage = makeLazyPage(() =>
  import('app/components/call/admin/LessonBookingTranscriptPage'));
const LessonBookingTranscriptPdfPage = makeLazyPage(() =>
  import('app/components/call/admin/LessonBookingTranscriptPage/TranscriptPdfPage'));
const CallEndedPage = makeLazyPage(() =>
  import('app/components/call/CallEndedPage'));
const IknowLoginCallbackPage = makeLazyPage(() =>
  import('app/components/auth/IknowLoginCallback'));
const Confirm = makeLazyPage(() =>
  import('app/components/pages/Confirm'));
const SendConfirmation = makeLazyPage(() =>
  import('app/components/pages/SendConfirmation'));
const SendUnlock = makeLazyPage(() =>
  import('app/components/pages/SendUnlock'));
const Signup = makeLazyPage(() =>
  import('app/components/pages/Signup'));
const Unlock = makeLazyPage(() =>
  import('app/components/pages/Unlock'));
const DailyNewsSearchPage = makeLazyPage(() =>
  import('app/containers/dailyNews/DailyNewsSearchPage'));
const DailyNewsCategoryPage = makeLazyPage(() =>
  import('app/containers/dailyNews/CategoryPage'));
const PublishedLessonPage = makeLazyPage(() =>
  import('app/containers/publishedLesson/PublishedLessonPage'));
const SpecificLessonPage = makeLazyPageBase(async () =>
  (await import('app/containers/publishedLesson/PublishedLessonPage')).SpecificLessonPage);
const DailyNewsArticlePage = makeLazyPage(() =>
  import('app/containers/dailyNews/DailyNewsArticlePage'));
const SpecificDailyNewsArticlePage = makeLazyPageBase(async () =>
  (await import('app/containers/dailyNews/DailyNewsArticlePage')).SpecificDailyNewsArticlePage);
const TimelinePage = makeLazyPage(() =>
  import('app/containers/dailyNews/TimelinePage'));
const LaunchMeetingPage = makeLazyPage(() =>
  import('app/components/call/meetings/LaunchMeetingPage'));
const JoinMeetingPage = makeLazyPage(() =>
  import('app/components/call/meetings/JoinMeetingPage'));
const ScreenSharingContentViewPage = makeLazyPage(() =>
  import('app/components/rtc/ScreenSharingContentView/ScreenSharingContentViewPage'));
const BibliographyEditorPage = makeLazyPage(() =>
  import('app/components/editor/bibliographies/BibliographyEditPage'));
const BibliographyListPage = makeLazyPage(() =>
  import('app/components/editor/bibliographies/BibliographyListPage'));

const TeacherRecruitmentSignUpPage = makeTeacherRecruitmentLazyLoadingPage(
  () => import('app/components/teacherRecruitment/TeacherRecruitmentSignUpPage'),
);
const TeacherRecruitmentPage = makeTeacherRecruitmentLazyLoadingPage(
  () => import('app/components/teacherRecruitment/TeacherRecruitmentPage'),
);
const TeacherRecruitmentHelpPage = makeTeacherRecruitmentLazyLoadingPage(
  () => import('app/components/teacherRecruitment/help/HelpPage'),
);
const TeacherRecruitmentContactPage = makeTeacherRecruitmentLazyLoadingPage(
  () => import('app/components/teacherRecruitment/help/ContactPage'),
);

const WordsWordPage = makeUknowLazyLoadingPage(() =>
  import('app/components/uknow/WordPage'));
const WordsAnswerEditorPage = makeLazyPage(() =>
  import('app/components/uknow/AnswerEditorPage'));
const WordsQuestionUploadPage = makeLazyPage(() =>
  import('app/components/uknow/QuestionUploadPage'));
const WordsWordListPage = makeUknowLazyLoadingPage(() =>
  import('app/components/uknow/WordListPage'));
const WordsQuestionListPage = makeUknowLazyLoadingPage(() => import('app/components/uknow/QuestionListPage'));

const makeWordsRouteTuple = (wordsRoutes: IWordsRoutes): Array<[IRoute, React.ComponentType<any>]> => {
  return [
    [wordsRoutes.Word, WordsWordPage],
    [wordsRoutes.AnswerEditor, WordsAnswerEditorPage],
    [wordsRoutes.QuestionUpload, WordsQuestionUploadPage],
    [wordsRoutes.WordList, WordsWordListPage],
    [wordsRoutes.QuestionList, WordsQuestionListPage],
  ];
};

import LazyCustomModule from 'app/components/reamaze/CustomModule/LazyCustomModule';
import { isTeacherSite } from 'app/constants/TeacherSite';
import { useHistoryLocation } from './router/History';
import { useBrand } from './OrganizationContext';
import { LanguageId } from 'app/constants/enums/Language/data';
import { ConfigurationForSiteFeatures } from 'app/constants/schemas/SiteFeatures/siteFeatureTypes';
import { isSiteFeatureId } from 'app/constants/enums/SiteFeature';
import { SiteFeatureId } from 'app/constants/enums/SiteFeature/data';
// End lazy routes
/* eslint-enable sort-imports */

const RedirectTeachOrDefault = () => {
  const location = useHistoryLocation();
  return isTeacherSite(location.hostname) ? (
    <Redirect to={Routes.TeacherRecruitmentRoute.getPathname()} />
  ) : (
    <RedirectBasedOnSiteFeatures />
  );
};

export const getRedirectForSiteFeatures = (features: ConfigurationForSiteFeatures, fallback: string) => {
  return features.users.enabled ? Routes.LoginRoute.getPathname() :
    features.dailyNews.enabled ? Routes.DailyNewsRoute.getPathname() :
    features.materials.enabled  ? Routes.MaterialsRoute.getPathname() :
    fallback;
};

const RedirectBasedOnSiteFeatures = () => {
  const { siteFeatures, dashboardUrl } = useBrand();
  const defaultUrl = dashboardUrl ?? Routes.NotFoundPageRoute.getPathname();
  const destination = getRedirectForSiteFeatures(
    siteFeatures,
    // Fallback to the dashboard url for this brand or the not found route if there's no dashboard url.
    defaultUrl ?? Routes.NotFoundPageRoute.getPathname(),
  );
  return (
    <Redirect to={destination} />
  );
};

/**
 * Redirects from the index routes. While in general these routes are unreachable,
 * on the teach site and in development, it is possible the land on these pages.
 *
 * This component simply catches those two paths and handles redirection
 */
const RedirectRoot: FC = ({ children }) => {
  const location = useHistoryLocation();
  if (location.pathname === '' || location.pathname === '/') {
    return <RedirectTeachOrDefault />;
  } else {
    return <>{children}</>;
  }
};

const RedirectWrongMarket = () => (
  <Redirect to={Routes.WrongBrandRoute.getPathname()} />
);

const RedirectOrganizationMaterials = ({ params: { languageId } }: { params: { languageId: LanguageId }}) => {
  const brand = useBrand();
  return (
    <Redirect
      to={Routes.BrandMaterialsRoute.getPathname({
        brandId: brand.id,
        languageId,
      })}
    />
  );
};

type RouteComponent = React.ComponentType<{
  params: IActiveRoute['params'],
  query: IActiveRoute['query'],
}>;

type RouteId = SiteFeatureId | 'always' | 'admin' | 'teacherRecruitment';
export const AllRoutesMap: {
  [customization in RouteId]: Array<[
    IRoute,
    // Using an `any` type for now because many of these components cause type
    // errors (such as the components that use JS).
    //
    // Rather than fixing all their types, it might be better to not pass the
    // query params as props altogether, and instead use the React context.
    React.ComponentType<any>
  ]>
} = {
  dailyNews: [
    [Routes.DailyNewsArticleRoute, DailyNewsArticlePage],
    [Routes.DailyNewsCategoryRoute, DailyNewsCategoryPage],
    [Routes.DailyNewsRoute, TimelinePage],
    [Routes.DailyNewsSearchRoute, DailyNewsSearchPage],
    [Routes.DailyNewsSpecificArticleRoute, SpecificDailyNewsArticlePage],
  ],
  // The iknow integration customization doesn't currently control any routes, but it is included
  // for completeness.
  iknowIntegration: [
  ],
  users: [
    [Routes.AttachDmmAuthenticationRoute, AttachDmmAuthPage],
    [Routes.ConfirmRoute, Confirm],
    [Routes.DmmLoginCallback, DmmLoginCallbackPage],
    [Routes.EditProfileRoute, EditProfilePage],
    [Routes.ForgotPasswordRoute, ForgotPassword],
    [Routes.IknowLoginCallback, IknowLoginCallbackPage],
    [Routes.IncompleteProfileRoute, IncompleteProfilePage],
    [Routes.LoginRoute, Login],
    [Routes.LogoutRoute, Logout],
    [Routes.OauthApplicationsRoute, OauthApplicationsPage],
    [Routes.OauthAttachCallback, OauthAttachCallbackPage],
    [Routes.OauthDelegateRoute, OauthDelegatePage],
    [Routes.OauthLoginCallback, OauthLoginCallbackPage],
    [Routes.OauthLoginPopupCallback, OauthLoginPopupCallbackPage],
    [Routes.ResetPasswordRoute, ResetPassword],
    [Routes.SendConfirmationRoute, SendConfirmation],
    [Routes.SendUnlockRoute, SendUnlock],
    [Routes.SignupRoute, Signup],
    [Routes.UnlockRoute, Unlock],
    [Routes.WrongBrandRoute, WrongBrandPage],
    [Routes.WrongMarketRoute, RedirectWrongMarket],
  ],
  materials: [
    [Routes.BrandCurriculumEditRoute, CurriculaEditorPage],
    [Routes.CurriculaListRoute, CurriculaListPage],
    [Routes.CategoriesListRoute, CategoriesListPage],
    [Routes.BrandMaterialsRoute, CategoriesPage],
    [Routes.CategoriesRoute, CategoriesPage],
    [Routes.CategoryRoute, CategoryPage],
    [Routes.CommonTextsRoute, CommonTextListPage],
    [Routes.CreateBannersRoute, BannerEditorPage],
    [Routes.DialogSpeakersRoute, DialogSpeakerListPage],
    [Routes.EditBannersRoute, BannerEditorPage],
    [Routes.EditCategoryCoursesRoute, CategoryCoursesEditorPage],
    [Routes.EditCategoryRoute, CategoryEditorPage],
    [Routes.EditCommonTextRoute, CommonTextEditor],
    [Routes.EditCourseLessonsRoute, CourseLessonsEditorPage],
    [Routes.EditCourseRoute, CourseEditorPage],
    [Routes.EditDialogSpeakerRoute, DialogSpeakerEditorPage],
    [Routes.EditLessonRoute, Editor],
    [Routes.EditWordRoute, EditWordPage],
    [Routes.EditorCoursesRoute, CourseListPage],
    [Routes.EditorLessonTemplatesRoute, LessonTemplateListPage],
    [Routes.EditorLessonsRoute, LessonListPage],
    [Routes.LevelsRoute, LevelsPage],
    [Routes.MaterialsRoute, CategoriesPage],
    [Routes.OrganizationMaterialsRoute, RedirectOrganizationMaterials],
    [Routes.PublishLessonRoute, PublishLesson],
    [Routes.SentenceEditorRoute, SentenceEditorPage],
    [Routes.SentenceListPageRoute, SentenceListPage],
    [Routes.ViewCurrentPublishedLessonRoute, PublishedLessonPage],
    [Routes.ViewLessonRoute, LessonPage],
    [Routes.ViewPublishedLessonRoute, SpecificLessonPage],
    [Routes.ViewVocabListRoute, ViewVocabListPage],
    [Routes.VocabListsAdminRoute, VocabListAdminPage],
    [Routes.WordAdminRoute, WordAdminPage],
  ],
  meetings: [
    [Routes.CallEndedRoute, CallEndedPage],
    [Routes.FullScreenCallRoute, FullScreenCallPage],
    [Routes.LessonBookingRoute, LessonBookingRedirectPage],
    [Routes.MeetingBookingNewRoute, MeetingBookingNewPage],
    [Routes.MeetingBookingRoute, MeetingBookingRedirectPage],
    [Routes.MeetingJoinRoute, JoinMeetingPage],
    [Routes.MeetingLandingRoute, LaunchMeetingPage],
    [Routes.PdfCallRoute, PdfCallPage],
    [Routes.ScreenSharingContentViewRoute, ScreenSharingContentViewPage],
    [Routes.SlackIntegrationCancelled, SlackIntegrationCancelledPage],
    [Routes.SlackIntegrationFailure, SlackIntegrationFailurePage],
    [Routes.SlackIntegrationSuccess, SlackIntegrationSuccessPage],
    [Routes.TestCallRoute, TestCallPage],
    [Routes.ViewTeacherRoute, TeacherViewPage],
  ],
  wordsPage: [
    // Embed words and uknow routes.
    ...makeWordsRouteTuple(UKnowRoutes),
    ...makeWordsRouteTuple(WordsRoutes),
  ],
  always: [
    [Routes.BaseRoute, RedirectBasedOnSiteFeatures],
    [Routes.RootRoute, RedirectBasedOnSiteFeatures],
    [Routes.ForbiddenPageRoute, Forbidden],
    [Routes.MaintenancePageRoute, Maintenance],
    [Routes.NotFoundPageRoute, NotFound],
  ],
  admin: [
    [Routes.BannersRoute, BannerListPage],
    [Routes.BibliographyEditorRoute, BibliographyEditorPage],
    [Routes.BibliographyListRoute, BibliographyListPage],
    [Routes.BrandCreateRoute, BrandCreatePage],
    [Routes.BrandEditorRoute, BrandEditorPage],
    [Routes.BrandMeetingsRoute, CreateBrandMeetings],
    [Routes.ColorPaletteRoute, ColorPalette],
    [Routes.CreateLessonBookingRoute, CreateLessonBookingPage],
    [Routes.CreateStaffMemberRoute, EditStaffPage],
    [Routes.CreateUserRoute, CreateUserPage],
    [Routes.DashboardRoute, Dashboard],
    [Routes.EditBlockedEmailDomainsRoute, BlockedEmailDomainEditor],
    [Routes.EditStaffMemberRoute, EditStaffPage],
    [Routes.EditUserRoute, EditUserPage],
    [Routes.EditorTeachersRoute, TeacherListPage],
    [Routes.LessonBookingTranscriptPdfRoute, LessonBookingTranscriptPdfPage],
    [Routes.LessonBookingTranscriptRoute, LessonBookingTranscriptPage],
    [Routes.LessonBookingsAdminRoute, LessonBookingsAdminPage],
    [Routes.MeetingBookingsAdminRoute, CreateBrandMeetings],
    [Routes.OauthApplicationsEditRoute, OauthApplicationsPage],
    [Routes.OrganizationCreateRoute, OrganizationCreatePage],
    [Routes.OrganizationEditorRoute, OrganizationEditorPage],
    [Routes.OrganizationListRoute, OrganizationListPage],
    [Routes.ReamazeCustomModuleRoute, LazyCustomModule],
    [Routes.StaffMembersRoute, StaffMembers],
    [Routes.StaffProfileRoute, StaffProfilePage],
    [Routes.UsersRoute, Users],
    [Routes.ViewLessonBookingRoute, ViewLessonBookingPage],
    [Routes.ViewUserRoute, UserViewPage],
  ],
  teacherRecruitment: [
    [Routes.TeacherRecruitmentContactRoute, TeacherRecruitmentContactPage],
    [Routes.TeacherRecruitmentHelpRoute, TeacherRecruitmentHelpPage],
    [Routes.TeacherRecruitmentRoute, TeacherRecruitmentPage],
    [Routes.TeacherRecruitmentSignUpRoute, TeacherRecruitmentSignUpPage],
  ],
};

export const getAllRoutes = (isFirstParty: boolean, siteFeatures: ConfigurationForSiteFeatures) => {
  const routeComponents = OrderedMap([
    ...Object.entries(siteFeatures).flatMap(([id, config]) => {
      if (isSiteFeatureId(id) && config.enabled) {
        return AllRoutesMap[id];
      } else {
        return [];
      }
    }),
    ...AllRoutesMap.always,
    ...(isFirstParty ? [
      ...AllRoutesMap.admin,
      ...AllRoutesMap.teacherRecruitment,
    ] : []),
  ]);
  return routeComponents;
};

const BASIC_ROUTES_MAP = OrderedMap(AllRoutesMap.always);
const SiteRoutesContext = createContext(BASIC_ROUTES_MAP);

/**
 * Raw context provider exported for tests.
 */
export const SiteRoutesProvider = SiteRoutesContext.Provider;
export const OrganizationConfiguredRoutesProvider: React.FC = ({ children }) => {
  const { siteFeatures, isFirstParty } = useBrand();
  return (
    <SiteRoutesProvider value={getAllRoutes(isFirstParty, siteFeatures)}>
      {children}
    </SiteRoutesProvider>
  );
};

export const useConfiguredRoutes = () => useContext(SiteRoutesContext);

export default function HandleRoute({ route, params, fallback }: IActiveRoute & {
  fallback: RouteComponent,
}) {
  const query = useQuery();
  const routeComponents = useConfiguredRoutes();

  const MatchedComponent = (
    route ? routeComponents.get(route, fallback) : fallback
  );

  // Put an <ErrorBoundaryPage> inside the <Router>, so that navigation still works even if
  // some page throws an error.
  return (
    <ErrorBoundaryPage>
      <RedirectRoot>
        <SiteWideRedirectMiddleware>
          <MatchedComponent params={params} query={query} />
        </SiteWideRedirectMiddleware>
      </RedirectRoot>
    </ErrorBoundaryPage>
  );
}
